import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { RootState, Site, LoggerService, ThemeIcon, MultiSelect, GroupedOption, CommonGlobalService, Option, UUID, usePersistantState, SiteIcon, CommonSiteService } from '@umanforge/common';

import { useState } from 'react';

import BaseModal from '@common_components/modals/baseModal.component';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import BottomModal from '@common_components/modals/bottomModal.component';
import InputText from '@common_components/inputs/textInput.component';
import ButtonSelectThemeColor from 'components/others/buttonSelectThemeColor.component';
import Tabs, { TabItem } from '@common_components/buttons/Tabs.component';

import { EditBoxIcon } from '@common_components/icons/editBox.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';
import { TitleIcon } from '@common_components/icons/title.icon';
import { EventManagerIcon } from '@common_components/icons/eventManager.icon';

import { Theme } from 'interfaces/theme.interface';
import { SiteTableResponse } from 'interfaces/types.interface';

import { EventManagerService } from 'services/eventManagers.service';
import { ThemeService } from 'services/themes.service';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';
import { KEY } from 'constants/local_storage_key.constant';

interface ThemeModalProps {
  creationOfTheme?: boolean,
  themeActive: Theme;
  elementIndex: number;
  disable?: boolean;
  onClose: () => void;
  onUpdate: ( action: 'update' | 'create' | 'merge' ) => void;
};

const ThemeModal: React.FC<ThemeModalProps> = ( {
  themeActive,
  disable,
  onClose,
  onUpdate,
} ) => {
  const logger: LoggerService = new LoggerService( 'ThemeModal' );
  const { t } = useTranslation();

  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();

  const [ clientId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_CLIENT_ID, undefined );
  const [ siteId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SITE_ID, undefined );
  const [ tableId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_EVENT_MANAGER_ID, undefined );

  const [ themeName, setThemeName ] = useState( themeActive.name ? themeActive.name : '' );
  const [ themeNewName, setThemeNewName ] = useState( '' );

  const [ eventManagersOption, setEventManagersOption ] = useState<GroupedOption<{ id: string, name: string }>[]>( [] );
  const [ selectedEventManager, setSelectedEventManager ] = useState<Option<{ id: string, name: string }>[]>( [] );

  const [ sitesOption, setSitesOption ] = useState<Option<Site>[]>( [] );
  const [ selectedSites, setSelectedSites ] = useState<Option<Site>[]>( [] );

  const [ allowedSites, setAllowedSites ] = useState<Site[]>( [] );

  const [ themeToMergeOption, setThemeToMergeOption ] = useState<Option<Theme>[]>( [] );
  const [ selectedThemeToMerge, setSelectedThemeToMerge ] = useState<Option<Theme>[]>( [] );

  const [ selectedPage, setSelectedPage ] = useState<number>( 0 );
  const [ selectedColor, setSelectedColor ] = useState<string>( themeActive.color ? themeActive.color : '' );
  const [ newSelectedColor, setNewSelectedColor ] = useState<string>( '' );

  const [ actualSites, setActualSites ] = useState<string[]>( [] );
  const [ actualEventManager, setActualEventManager ] = useState<string[]>( [] );

  const [ formChanged, setFormChanged ] = useState<boolean>( false );
  const [ formValid, setFormValid ] = useState<boolean>( false );
  const [ formMergeValid, setFormMergeValid ] = useState<boolean>( false );

  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );
  const [ isReadOnly, setIsReadOnly ] = useState<boolean>( disable ?? false );

  const [ openDeleteModal, setOpenDeleteModal ] = useState<boolean>( false );

  const pagesModify: TabItem[] = [
    { id: 'modification', name: t( 'common:modification' ) },
    { id: 'fusion', name: t( 'common:fusion' ) }
  ];

  const pagesCreate: TabItem[] = [
    { id: 'creation', name: t( 'common:creation' ) }
  ];

  useEffect( () => {
    if ( isFirstRender && clientId && siteId && tableId ) {
      init()
        .then( () => {
          setIsFirstRender( false );
        } );
    }
  }, [ clientId, siteId, tableId ] );

  useEffect( () => {
    if ( !isFirstRender && selectedSites ) {
      const updateEventManagersAndThemes = async () => {
        const allowedSiteIdsForThemeRequest: UUID[] = [];

        for ( const siteOption of selectedSites ) {
          const selectedSiteId = siteOption.object.id;
          let isAllowed = false;

          for ( const site of allowedSites ) {
            if ( site.id === selectedSiteId ) {
              isAllowed = true;
              break;
            }
          }

          if ( isAllowed ) {
            allowedSiteIdsForThemeRequest.push( selectedSiteId );
          }
        }

        if ( allowedSiteIdsForThemeRequest.length === 0 ) {
          setEventManagersOption( [] );
          setSelectedEventManager( [] );
          setThemeToMergeOption( [] );
          setSelectedThemeToMerge( [] );
          return;
        }

        const responseEventManagers : SiteTableResponse = await EventManagerService.getTablesBySiteIDs( allowedSiteIdsForThemeRequest, token, dispatch );

        const groupedEventManagers: GroupedOption<{ id: string, name: string, isAdmin: boolean }> [] = [];
        const allEventManagersOptions: Option<{ id: string, name: string, isAdmin: boolean }> [] = [];

        for ( const [ , site ] of Object.entries( responseEventManagers ) ) {
          const baseOptions = CommonGlobalService.toOptions( site.eventManagers ) as Option<{ id: string, name: string, isAdmin: boolean }> [];
          const optionsWithAdminFlag: Option<{ id: string, name: string, isAdmin: boolean }> [] = [];

          for ( const option of baseOptions ) {
            optionsWithAdminFlag.push( { ...option, isDisabled: !option.object.isAdmin } );
          }

          groupedEventManagers.push( { label: site.name, options: optionsWithAdminFlag } );

          for ( const option of optionsWithAdminFlag ) {
            allEventManagersOptions.push( option );
          }
        }

        const idOnOptions = <T extends { id: UUID }>( option: Option<T>, id: UUID ) => {
          return option.object.id === id;
        };

        setEventManagersOption( groupedEventManagers );
        setSelectedEventManager( previousSelectedEventManagers => {
          const selectedEventManagersOption: Option<{ id: string; name: string; isAdmin: boolean; }>[] = groupedEventManagers
            .map( eventManager => { return eventManager.options; } )
            .flat()
            .filter( eventManager => {
              return previousSelectedEventManagers.some( option => { return idOnOptions( option, eventManager.object.id ); } );
            } );
          const eventManagersIdNotAllow = previousSelectedEventManagers
            .map( previousSelectedEventManager => { return previousSelectedEventManager.object.id; } )
            .filter( id => {
              return !selectedEventManagersOption.some( option => { return idOnOptions( option, id ); } );
            } );
          const eventManagersNotAllow: typeof selectedEventManagersOption = CommonGlobalService.toOptions(
            eventManagersIdNotAllow
              .map( eventManagerIdNotAllow => {
                const eventManagerNotAllow: { id: string; name: string; isAdmin: boolean; } = {
                  id: eventManagerIdNotAllow,
                  name: t( 'event_manager_unknow' ),
                  isAdmin: false
                };
                return eventManagerNotAllow;
              } )
          )
            .map( option => { return { ...option, color: '#ff000088' }; } );
          selectedEventManagersOption.push( ...eventManagersNotAllow );

          return selectedEventManagersOption;
        } );

        const themesBySites = await ThemeService.getThemesBySites( allowedSiteIdsForThemeRequest, token, dispatch );
        const updatedThemeOptions: Option<Theme>[] = CommonGlobalService.toOptions( themesBySites );

        setThemeToMergeOption( updatedThemeOptions );
        setSelectedThemeToMerge( previousSelectedThemes => {
          const filtered: Option<Theme>[] = [];

          for ( const previousTheme of previousSelectedThemes ) {
            let exists = false;

            for ( const option of updatedThemeOptions ) {
              if ( option.object.id === previousTheme.object.id ) {
                exists = true;
                break;
              }
            }

            if ( exists ) {
              filtered.push( previousTheme );
            }
          }

          return filtered;
        } );
      };

      updateEventManagersAndThemes();
    }
  }, [ selectedSites, allowedSites ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const sortAndJoinArray = <T, >( array: T[] ): string => {
        return array.slice().sort().join( '' );
      };

      const canPut = themeName != '' &&
        selectedColor != '' &&
        selectedSites.length > 0 &&
        selectedEventManager.length > 0;
      const isNameChange = themeName != themeActive.name;
      const isColorChange = selectedColor != themeActive.color;
      const isSitesChange = sortAndJoinArray( selectedSites.map( site => { return site.object.id; } ) ) != sortAndJoinArray( actualSites );
      const isEventManagersChange = sortAndJoinArray( selectedEventManager.map( eventManager => { return eventManager.object.id; } ) ) != sortAndJoinArray( actualEventManager );

      setFormChanged(
        isNameChange ||
        isColorChange ||
        isSitesChange ||
        isEventManagersChange
      );

      setFormValid(
        canPut && (
          isNameChange ||
          isColorChange ||
          isSitesChange ||
          isEventManagersChange
        )
      );
    }
  }, [ selectedEventManager, selectedSites, selectedColor, themeName ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const isValidMerge = selectedThemeToMerge.length > 1 && newSelectedColor != '' && themeNewName != '';
      setFormMergeValid( isValidMerge );
    }
  }, [ selectedThemeToMerge, newSelectedColor, themeNewName ] );

  const init = async () => {
    if ( me ) {

      const sites: Site[] = [];
      const sitesIdOfActiveTheme: UUID[] = [];
      const eventManagersIdOfActiveTheme: UUID[] = [];
      if ( themeActive.id ) {
        const responseActiveTheme : Theme = await ThemeService.getThemeById( themeActive.id, token, dispatch );
        sitesIdOfActiveTheme.push( ...( responseActiveTheme.siteIds ?? [] ) );
        eventManagersIdOfActiveTheme.push( ...( responseActiveTheme.eventManagerIds || [] ) );
      } else {
        if ( siteId && tableId ) {
          sitesIdOfActiveTheme.push( siteId );
          eventManagersIdOfActiveTheme.push( tableId );
        }
      }

      if ( clientId ) {
        const updatedSites = ( await CommonSiteService.getAllowedSitesByClientsId( [ clientId ], token, dispatch ) )
          .map( site => {
            site.isAdmin = CommonGlobalService.isAdminOfSite( me, site.id );
            return site;
          } );
        sites.push( ...updatedSites );
      }

      setAllowedSites( sites );

      const updatedSitesOption = CommonGlobalService.toOptions( sites )
        .map( option => { return { ...option, isDisabled: !option.object.isAdmin }; } );
      const selectedSitesOption = updatedSitesOption.filter( site => { return sitesIdOfActiveTheme.includes( site.object.id ); } );

      const responseEventManagers : SiteTableResponse = await EventManagerService.getTablesBySiteIDs( selectedSitesOption.map( site => { return site.object.id; } ), token, dispatch );
      const groupedEventManagers = Object.entries( responseEventManagers ).map( ( [ , site ] ) => {
        const options = CommonGlobalService.toOptions( site.eventManagers )
          .map( option => { return { ...option, isDisabled: !option.object.isAdmin }; } );
        return { label: site.name, options: options };
      } );

      const sitesIdNotAllow = sitesIdOfActiveTheme.filter( id => { return !selectedSitesOption.some( option => { return option.object.id === id; } ); } );
      const sitesNotAllow = CommonGlobalService.toOptions( sitesIdNotAllow
        .map( siteIdNotAllow => {
          const siteNotAllow: Site = {
            id: siteIdNotAllow,
            clientId: '',
            name: t( 'site_unknow' ),
            address: '',
            additionalAddress: '',
            zipCode: '',
            city: '',
            country: '',
            isAdmin: false,
            sectors: undefined,
            sftpPublicKey: null,
            sftpUsername: null
          };
          return siteNotAllow;
        } )
      )
        .map( option => { return { ...option, color: '#ff000088' }; } );
      selectedSitesOption.push( ...sitesNotAllow );

      const selectedEventManagersOption: Option<{ id: string; name: string; isAdmin: boolean; }>[] = groupedEventManagers
        .map( eventManager => { return eventManager.options; } )
        .flat()
        .filter( eventManager => { return eventManagersIdOfActiveTheme.includes( eventManager.object.id ); } );
      const eventManagersIdNotAllow = eventManagersIdOfActiveTheme.filter( id => { return !selectedEventManagersOption.some( option => { return option.object.id === id; } ); } );
      const eventManagersNotAllow: typeof selectedEventManagersOption = CommonGlobalService.toOptions( eventManagersIdNotAllow
        .map( eventManagerIdNotAllow => {
          const eventManagerNotAllow: { id: string; name: string; isAdmin: boolean; } = {
            id: eventManagerIdNotAllow,
            name: t( 'event_manager_unknow' ),
            isAdmin: false
          };
          return eventManagerNotAllow;
        } )
      )
        .map( option => { return { ...option, color: '#ff000088' }; } );
      selectedEventManagersOption.push( ...eventManagersNotAllow );

      setSitesOption( updatedSitesOption );
      setSelectedSites( selectedSitesOption );
      setActualSites( sitesIdOfActiveTheme );
      setEventManagersOption( groupedEventManagers );
      setSelectedEventManager( selectedEventManagersOption );
      setActualEventManager( eventManagersIdOfActiveTheme );

      if ( !disable ) {
        setIsReadOnly( selectedEventManagersOption.some( option => { return !option.object.isAdmin; } ) || ( eventManagersIdNotAllow.length > 0 ) );
      }

      const allowedSiteIdsForThemeRequest: UUID[] = selectedSitesOption
        .filter( option => { return sites.some( site => { return site.id === option.object.id; } ); } )
        .map( site => { return site.object.id; } );

      const updatedThemeOptions: Option<Theme>[] = allowedSiteIdsForThemeRequest.length === 0
        ? []
        : CommonGlobalService.toOptions( await ThemeService.getThemesBySites( allowedSiteIdsForThemeRequest, token, dispatch ) );

      setThemeToMergeOption( updatedThemeOptions );
      setSelectedThemeToMerge( updatedThemeOptions.filter( theme => { return theme.object.id === themeActive.id; } ) );
    }
  };

  //----------- Selected Tab Visible ---------------\\
  const handleTabSelect = ( index: number ) => {
    setSelectedPage( index );
  };

  //----------- On Click Delete Event Manager => delete Event Manager and redirect to "/Home" ---------------\\
  const handleDeleteClicked = () => {
    setOpenDeleteModal( true );
  };

  //----------- DELETE ---------------\\
  const handleDelete = async () => {
    try {
      await ThemeService.deleteTheme( themeActive.id, token, dispatch );
      onClose();
    } catch ( error ) {
      logger.error( 'An error occurred while deleting the theme:', error );
    }
  };

  //----------- On Click Create Event Manager => create Event Manager ---------------\\
  const handleCreate = async () => {
    const newThemeData = {
      name: themeName,
      color: selectedColor,
      eventManagerIds: selectedEventManager.map( item => { return item.object.id; } ),
      siteIds: selectedSites.map( item => { return item.object.id; } ),
    };
    try {
      await ThemeService.createTheme( newThemeData, token, dispatch );
      if ( onUpdate ) { onUpdate( 'create' ); }
      onClose();
    } catch ( error ) {
      logger.error( 'An error occurred while creating the theme:', error );
    }
  };

  //----------- On Click Update Event Manager => update Event Manager ---------------\\
  const handleUpdate = async () => {
    const newThemeData: Theme = {
      id: themeActive.id,
      name: themeName,
      color: selectedColor,
      eventManagerIds: selectedEventManager.map( item => { return item.object.id; } ),
      siteIds: selectedSites.map( item => { return item.object.id; } ),
    };
    try {
      await ThemeService.updateTheme( newThemeData, token, dispatch );
      if ( onUpdate ) { onUpdate( 'update' ); }
      onClose();
    } catch ( error ) {
      logger.error( 'An error occurred while updating the theme:', error );
    }
  };

  const handleMerge = async () => {
    try {
      await ThemeService.mergeThemes( selectedThemeToMerge.map( item => { return item.object.id; } ), themeNewName, newSelectedColor, token, dispatch );
      if ( onUpdate ) { onUpdate( 'merge' ); }
      onClose();
    } catch ( error ) {
      logger.error( 'An error occurred while merging themes:', error );
    }
  };


  return (
    <BaseModal
      isOpen
      title={ themeActive.id ? t( 'translation:modification_of_a_theme' ) : t( 'translation:creation_of_a_theme' ) }
      icon={ themeActive.id ? <EditBoxIcon className='bg-orange-200 p-2 text-orange-800 rounded-md' size={10} /> : <PlusIcon className='bg-green-200 text-green-800 p-2 rounded-md' size={10} /> }
      onClose={ onClose }
      noConfirmationModal={ !formChanged }
      footer={
        <>
          {
            !isReadOnly && (
              <BottomModal
                tableId={ themeActive.id }
                formValid={ selectedPage === 0 ? formValid : formMergeValid }
                onCreate={ handleCreate }
                onUpdate={ selectedPage === 0 ? handleUpdate : handleMerge }
                onDelete={ selectedPage === 0 ? handleDeleteClicked : undefined }
              />
            )
          }
        </>
      }
    >
      <div className="py-5 px-7 gap-y-4  bg-white-base rounded-md shadow-md">
        {
          !isReadOnly && (
            <Tabs
              items={ themeActive.id ? pagesModify : pagesCreate }
              activeItem={ selectedPage }
              onItemClick={ handleTabSelect }
            />
          )
        }

        <div className='overflow-visible'>
          <div className={`py-5 px-7 gap-y-4 flex flex-col rounded-b-xl shadow-md font-texte ${selectedPage === 0 ? '' : 'hidden'}`}>

            <InputText
              label={ t( 'common:name' ) }
              icon={ <TitleIcon size={7}/> }
              value={ themeName }
              onChange={ setThemeName }
              maxCharacter={ MAX_LENGTH_ENTITY.THEME_NAME }
              labelPosition='top'
              disable={ isReadOnly }
              isRequired
            />

            <MultiSelect
              label={ t( 'common:site' ) }
              icon={ <SiteIcon className='h-7 w-7'/> }
              getValues={ sitesOption }
              selectedValues={ selectedSites }
              onChange={ setSelectedSites }
              disabled={ isReadOnly }
              clearable={ false }
              required
            />

            <MultiSelect
              label={ t( 'translation:event_manager' ) }
              icon={ <EventManagerIcon className='h-7 w-7'/> }
              getValues={ eventManagersOption }
              selectedValues={ selectedEventManager }
              onChange={ setSelectedEventManager }
              clearable={ false }
              disabled={ isReadOnly }
            />

            <ButtonSelectThemeColor
              label={t( 'common:color' )}
              defaultValue={selectedColor}
              onSelectionChange={setSelectedColor}
              labelPosition='top'
              disable={ isReadOnly }
            />

          </div>
          {
            !isReadOnly &&
            <div className={`py-5 px-7 gap-y-4 flex flex-col rounded-b-xl shadow-md font-texte ${selectedPage === 1 ? '' : 'hidden'}`}>

              <InputText
                icon={ <TitleIcon size={7}/> }
                label={ t( 'translation:new_name' ) }
                value={ themeNewName }
                onChange={ setThemeNewName }
                labelPosition='top'
                isRequired
              />

              <MultiSelect
                label={ t( 'translation:theme_to_merge' ) }
                icon={ <ThemeIcon/> }
                getValues={ themeToMergeOption }
                selectedValues={ selectedThemeToMerge }
                onChange={ setSelectedThemeToMerge }
              />

              <ButtonSelectThemeColor
                labelPosition='top'
                label={t( 'common:color' )}
                onSelectionChange={ setNewSelectedColor }
              />

            </div>
          }
        </div>
      </div>
      {
        !isReadOnly && (
          <ConfirmationModal
            isOpen={openDeleteModal}
            onConfirm={handleDelete}
            onCancel={() => {return ( setOpenDeleteModal( false ) );}}
            title={t( 'translation:delete_theme' )}
            description={t( 'translation:are_you_sure_that_you_wanna_delete_the_theme_it_is_irreversible' )}
          />
        )
      }
    </BaseModal>
  );
};

export default ThemeModal;
